import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { userError, userSuccess } from "../../utils/toastUtils";

const OTP_EXPIRY_SECONDS = 120;

const OtpLoginForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const { requestOtp, resendOtp, verifyOtp, completeRegistration } = useAuth();

  const [step, setStep] = useState("phone");
  // phone | otp | register

  const [loading, setLoading] = useState(false);
  const [expiresIn, setExpiresIn] = useState(0);

  const [formData, setFormData] = useState({
    phoneNumber: "",
    otp: ["", "", "", ""],
    name: "",
    email: "",
  });

  const otpLength = 4;
  const otpRefs = useRef([]);

  const isOtpComplete = formData.otp.every((d) => d !== "");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setFormData((p) => ({ ...p, phoneNumber: value }));
  };

  const handleOtpChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...formData.otp];
    newOtp[index] = value;
    setFormData((p) => ({ ...p, otp: newOtp }));

    if (value && index < otpLength - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !formData.otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }

    if (e.key === "Enter" && isOtpComplete) {
      handleSubmit(e);
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData("text").slice(0, otpLength);
    if (!/^[0-9]+$/.test(paste)) return;

    const pasteArray = paste.split("");
    const newOtp = [...formData.otp];

    pasteArray.forEach((digit, i) => {
      newOtp[i] = digit;
    });

    setFormData((p) => ({ ...p, otp: newOtp }));

    otpRefs.current[pasteArray.length - 1]?.focus();
  };

  useEffect(() => {
    if (expiresIn <= 0) return;

    const timer = setInterval(() => {
      setExpiresIn((s) => s - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresIn]);

  const handleRequestOtp = async () => {
    setLoading(true);
    try {
      const res = await requestOtp(formData.phoneNumber);
      setExpiresIn(res?.expiresIn || OTP_EXPIRY_SECONDS);
      setStep("otp");
      userSuccess("OTP sent");
    } catch (err) {
      userError(err?.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const res = await resendOtp(formData.phoneNumber);
      setExpiresIn(res?.expiresIn || OTP_EXPIRY_SECONDS);
      userSuccess("OTP resent");
    } catch (err) {
      userError(err?.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    try {
      const res = await verifyOtp({
        phoneNumber: formData.phoneNumber,
        otp: formData.otp.join(""),
      });

      if (!res.isRegistered) {
        setStep("register");
        return;
      }

      onSuccess ? onSuccess() : navigate("/");
    } catch (err) {
      userError(err?.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async () => {
    setLoading(true);
    try {
      await completeRegistration({
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        gender: "female",
      });

      onSuccess ? onSuccess() : navigate("/");
    } catch (err) {
      userError(err?.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (step === "phone") return handleRequestOtp();
    if (step === "otp") return handleVerifyOtp();
    if (step === "register") return handleCompleteRegistration();
  };

  const inputFieldClasses = `
  w-full rounded-lg px-3 py-3
  outline-none
  ring-1 ring-brandAccentColor
  bg-white/60 backdrop-blur-md
  text-gray-900

  focus:bg-white/80
  focus:ring-primaryColor

  disabled:bg-gray-100
  disabled:backdrop-blur-0
  disabled:text-gray-500
  disabled:ring-gray-300
  disabled:cursor-not-allowed

  transition
`;

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-sm mx-auto w-full space-y-6 p-4 md:p-6 lg:p-8"
    >
      {/* Header */}
      <div>
        <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
          {step === "phone"
            ? "Login with OTP"
            : step === "otp"
            ? "Enter OTP"
            : "Complete Registration"}
        </h2>
        <p className="text-sm text-secondaryTextColor mt-1">
          {step === "phone"
            ? "Enter your phone number to continue"
            : step === "otp"
            ? "Enter the OTP sent to your phone"
            : "Tell us a bit about yourself"}
        </p>
      </div>

      {/* PHONE */}
      {step === "phone" && (
        <input
          type="tel"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handlePhoneChange}
          maxLength={10}
          inputMode="numeric"
          placeholder="Phone Number"
          required
          className={inputFieldClasses}
        />
      )}

      {/* OTP */}
      {step === "otp" && (
        <>
          {/* OTP BOXES */}
          <div className="flex justify-center gap-3" onPaste={handleOtpPaste}>
            {formData.otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (otpRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                className="
            w-12 h-12
            text-center text-lg font-semibold
            rounded-lg
            ring-1 ring-brandAccentColor
            focus:ring-primaryColor
            outline-none
          "
              />
            ))}
          </div>

          {/* TIMER + RESEND */}
          <div className="flex justify-between text-xs lg:text-sm">
            <span className="text-gray-500">
              {expiresIn > 0 ? `Expires in ${expiresIn}s` : "OTP expired"}
            </span>

            <button
              type="button"
              disabled={expiresIn > 0 || loading}
              onClick={handleResendOtp}
              className="text-[#f0653a] disabled:opacity-50"
            >
              Resend OTP
            </button>
          </div>
        </>
      )}

      {/* REGISTER */}
      {step === "register" && (
        <>
          <input
            type="tel"
            value={formData.phoneNumber}
            disabled
            className={inputFieldClasses}
          />

          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Full Name"
            required
            className={inputFieldClasses}
          />

          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email"
            required
            className={inputFieldClasses}
          />

        </>
      )}

      <button
        type="submit"
        disabled={loading || (step === "otp" && !isOtpComplete)}
        className="w-full py-2 bg-[#f0653a] text-white rounded-lg disabled:opacity-50"
      >
        {loading ? "Please wait..." : "Continue"}
      </button>
    </form>
  );
};

export default OtpLoginForm;
